import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';
import { tokens, chartColor } from '../../theme/tokens';

type Fmt = (v: number) => string;
const identity: Fmt = (v) => `${v}`;

const axisTick = { fontSize: 12, fill: tokens.color.textSecondary };
const gridStroke = tokens.color.border;

// ---- Shared clean tooltip --------------------------------------------------

const NexusTooltip: React.FC<any> = ({ active, payload, label, valueFormatter = identity }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <Box
      sx={{
        bgcolor: tokens.color.surface,
        border: `1px solid ${tokens.color.border}`,
        borderRadius: `${tokens.radius.md}px`,
        boxShadow: tokens.shadow.pop,
        px: 1.5,
        py: 1,
        minWidth: 120,
      }}
    >
      {label != null && (
        <Typography sx={{ fontSize: 12, fontWeight: 600, mb: 0.5 }}>{label}</Typography>
      )}
      {payload.map((p: any, i: number) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 12 }}>
          <Box sx={{ width: 9, height: 9, borderRadius: '2px', bgcolor: p.color || p.fill }} />
          <Typography sx={{ fontSize: 12, color: tokens.color.textSecondary, flex: 1 }}>{p.name}</Typography>
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{valueFormatter(p.value)}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const legendStyle = { fontSize: 12, color: tokens.color.textSecondary };

// ---- Bar chart -------------------------------------------------------------

export interface Series {
  key: string;
  name: string;
  color?: string;
}

export const NexusBarChart: React.FC<{
  data: any[];
  xKey: string;
  series: Series[];
  valueFormatter?: Fmt;
  height?: number;
  stacked?: boolean;
  /** Show value labels directly on the bars (default true for single-series). */
  showLabels?: boolean;
  yWidth?: number;
}> = ({ data, xKey, series, valueFormatter = identity, height = 280, stacked, showLabels, yWidth = 52 }) => {
  const labels = showLabels ?? series.length === 1;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 18, right: 12, left: 4, bottom: 4 }} barCategoryGap="22%">
        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
        <XAxis dataKey={xKey} tick={axisTick} tickLine={false} axisLine={{ stroke: gridStroke }} interval={0} />
        <YAxis
          width={yWidth}
          tick={axisTick}
          tickLine={false}
          axisLine={false}
          tickFormatter={(v: any) => valueFormatter(Number(v))}
        />
        <Tooltip cursor={{ fill: 'rgba(15,17,21,0.04)' }} content={<NexusTooltip valueFormatter={valueFormatter} />} />
        {series.length > 1 && <Legend wrapperStyle={legendStyle} iconType="circle" iconSize={9} />}
        {series.map((s, i) => (
          <Bar
            key={s.key}
            dataKey={s.key}
            name={s.name}
            fill={s.color || chartColor(i)}
            stackId={stacked ? 'stack' : undefined}
            radius={stacked ? 0 : [4, 4, 0, 0]}
            maxBarSize={56}
          >
            {labels && (
              <LabelList
                dataKey={s.key}
                position="top"
                formatter={(v: any) => valueFormatter(Number(v))}
                style={{ fontSize: 11, fontWeight: 600, fill: tokens.color.text }}
              />
            )}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

// ---- Donut chart with always-visible legend table --------------------------

export interface Slice {
  name: string;
  value: number;
  color?: string;
}

export const NexusDonut: React.FC<{
  data: Slice[];
  valueFormatter?: Fmt;
  height?: number;
  centerLabel?: string;
  centerValue?: string;
}> = ({ data, valueFormatter = identity, height = 240, centerLabel, centerValue }) => {
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
      <Box sx={{ position: 'relative', width: height, height, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="62%"
              outerRadius="100%"
              paddingAngle={1.5}
              stroke="none"
            >
              {data.map((d, i) => (
                <Cell key={i} fill={d.color || chartColor(i)} />
              ))}
            </Pie>
            <Tooltip content={<NexusTooltip valueFormatter={valueFormatter} />} />
          </PieChart>
        </ResponsiveContainer>
        {(centerValue || centerLabel) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
            }}
          >
            {centerValue && <Typography sx={{ fontWeight: 700, fontSize: 20, letterSpacing: '-0.02em' }}>{centerValue}</Typography>}
            {centerLabel && (
              <Typography sx={{ fontSize: 11, color: tokens.color.textSecondary, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {centerLabel}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* Always-visible legend table — name, value, share. No hover needed. */}
      <Box sx={{ flex: 1, minWidth: 180 }}>
        {data.map((d, i) => {
          const pct = total > 0 ? (Number(d.value) / total) * 100 : 0;
          return (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.6,
                borderBottom: i < data.length - 1 ? `1px solid ${tokens.color.border}` : 'none',
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: d.color || chartColor(i), flexShrink: 0 }} />
              <Typography sx={{ fontSize: 13, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.name}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{valueFormatter(Number(d.value) || 0)}</Typography>
              <Typography sx={{ fontSize: 12, color: tokens.color.textMuted, width: 42, textAlign: 'right' }}>
                {pct.toFixed(0)}%
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

// ---- Line / area trend -----------------------------------------------------

export const NexusTrend: React.FC<{
  data: any[];
  xKey: string;
  series: Series[];
  valueFormatter?: Fmt;
  height?: number;
  area?: boolean;
  yWidth?: number;
}> = ({ data, xKey, series, valueFormatter = identity, height = 280, area, yWidth = 52 }) => {
  const Chart: any = area ? AreaChart : LineChart;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <Chart data={data} margin={{ top: 12, right: 16, left: 4, bottom: 4 }}>
        <defs>
          {series.map((s, i) => (
            <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={s.color || chartColor(i)} stopOpacity={0.18} />
              <stop offset="100%" stopColor={s.color || chartColor(i)} stopOpacity={0} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={gridStroke} vertical={false} />
        <XAxis dataKey={xKey} tick={axisTick} tickLine={false} axisLine={{ stroke: gridStroke }} />
        <YAxis width={yWidth} tick={axisTick} tickLine={false} axisLine={false} tickFormatter={(v: any) => valueFormatter(Number(v))} />
        <Tooltip content={<NexusTooltip valueFormatter={valueFormatter} />} />
        {series.length > 1 && <Legend wrapperStyle={legendStyle} iconType="circle" iconSize={9} />}
        {series.map((s, i) =>
          area ? (
            <Area
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={s.color || chartColor(i)}
              strokeWidth={2}
              fill={`url(#grad-${s.key})`}
              dot={false}
            />
          ) : (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.name}
              stroke={s.color || chartColor(i)}
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4 }}
            />
          )
        )}
      </Chart>
    </ResponsiveContainer>
  );
};
